import type {Router} from 'vue-router';

type Handler = () => void;

let handler: Handler | null = null;

/**
 * Registered once at startup, in `main.ts`. A seam rather than a direct import: the
 * session store reaches the API through `api-client`, so importing the store here would
 * close the circle, and `api-client` would need a router it has no other reason to know
 * about.
 */
export const onSessionExpired = (next: Handler | null): void => {
  handler = next;
};

/** Silent before anything is registered: a call during startup must not throw. */
export const reportSessionExpired = (): void => {
  handler?.();
};

export interface ExpiryDeps {
  session: {expire: () => void};
  router: Pick<Router, 'replace'> & {
    currentRoute: {value: {fullPath: string; meta: {public?: boolean}; matched: readonly unknown[]}};
  };
}

/**
 * What a lapsed session does to the interface. Without this the store still believes it
 * is authenticated, so the route guard never fires and every further click fails the same
 * way with nothing the reader can act on.
 */
export const expireSession = ({session, router}: ExpiryDeps): void => {
  session.expire();

  const current = router.currentRoute.value;

  // Nothing matched means the first navigation has not been confirmed: `currentRoute` is
  // still START_LOCATION, whose empty meta reads as "private" for every destination. The
  // guard that is already running knows where the reader is actually going, and redirects
  // there itself if it has to — including with a `redirect` that points at the real page
  // rather than at `/`.
  if (current.matched.length === 0) return;

  // The password reset is reachable without a session on purpose; bouncing that reader to
  // the login screen would take away the page that can help them.
  if (current.meta.public) return;

  // A failure here is a navigation vue-router refused — one already under way, or the
  // same one twice because several requests failed together. Nothing to do about it.
  void router.replace({name: 'login', query: {redirect: current.fullPath}}).catch(() => undefined);
};
